import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Container,
    Grid,
    Paper,
    Tabs,
    Tab,
    CircularProgress,
    IconButton,
    Button
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Event as EventIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { eventApi, requestApi, UserRequest } from '../utils/api';
import { Event } from '../types/event';
import EventCard from '../components/EventCard';
import { eventBus, EVENTS } from '../utils/events';

const MyEvents: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [upcomingGoingEvents, setUpcomingGoingEvents] = useState<Array<{ event: Event, status: string }>>([]);
    const [pastGoingEvents, setPastGoingEvents] = useState<Array<{ event: Event, status: string }>>([]);
    const [upcomingHostingEvents, setUpcomingHostingEvents] = useState<Event[]>([]);
    const [pastHostingEvents, setPastHostingEvents] = useState<Event[]>([]);

    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date());

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/');
            return;
        }

        if (user) {
            loadUserEvents();

            const unsubscribe = eventBus.on(EVENTS.EVENT_UPDATED, () => {
                loadUserEvents();
            });

            return () => {
                unsubscribe();
            };
        }
    }, [user, isAuthenticated, navigate]);

    const loadUserEvents = async () => {
        if (!user) return;

        try {
            setLoading(true);

            const [userRequests, myCreatedEvents]: [UserRequest[], Event[]] = await Promise.all([
                requestApi.getUserRequests(),
                eventApi.getMyEvents()
            ]);

            const now = new Date();

            // Process Hosting Events
            const upcomingHosting = myCreatedEvents.filter(event => new Date(event.time) > now);
            const pastHosting = myCreatedEvents.filter(event => new Date(event.time) <= now);

            setUpcomingHostingEvents(upcomingHosting);
            setPastHostingEvents(pastHosting);

            // Process Going Events
            const allUserEventData: Array<{ eventId: string, status: string }> = [];

            userRequests.forEach((request: UserRequest) => {
                if (request.user_id === user.id) {
                    allUserEventData.push({
                        eventId: request.event_id,
                        status: request.status
                    });
                }
            });

            const goingEventsPromises = allUserEventData.map(async ({ eventId, status }) => {
                try {
                    const event = await eventApi.getEventById(eventId);
                    return { event, status };
                } catch (err) {
                    console.warn(`Failed to fetch event ${eventId}:`, err);
                    return null;
                }
            });

            const goingEventsResults = await Promise.all(goingEventsPromises);
            const validGoingEvents = goingEventsResults.filter((item): item is { event: Event, status: string } => item !== null);

            const upcomingGoing = validGoingEvents.filter(item => {
                const isFuture = new Date(item.event.time) > now;
                const isValidStatus = item.status === 'ACCEPTED' || item.status === 'SUBMITTED' || item.status === 'PENDING';
                return isFuture && isValidStatus;
            });

            const pastGoing = validGoingEvents.filter(item => new Date(item.event.time) <= now);

            setUpcomingGoingEvents(upcomingGoing);
            setPastGoingEvents(pastGoing);

        } catch (err) {
            console.error('Error loading events:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    // Calendar render helpers
    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month, 1).getDay();
    };

    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days = [];

        // Helper to format date as YYYY-MM-DD for comparison
        const formatDateKey = (date: Date) => {
            return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        };

        // Create sets of event dates for efficient lookup
        const hostingDates = new Set([
            ...upcomingHostingEvents,
            ...pastHostingEvents
        ].map(e => formatDateKey(new Date(e.time))));

        const goingDates = new Set([
            ...upcomingGoingEvents,
            ...pastGoingEvents
        ].map(item => formatDateKey(new Date(item.event.time))));

        // Empty cells for days before the first day of the month
        for (let i = 0; i < firstDay; i++) {
            days.push(<Box key={`empty-${i}`} sx={{ width: 36, height: 36 }} />);
        }

        // Days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            const dateToCheck = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
            const dateKey = formatDateKey(dateToCheck);

            const isToday =
                i === new Date().getDate() &&
                currentDate.getMonth() === new Date().getMonth() &&
                currentDate.getFullYear() === new Date().getFullYear();

            const isHosting = hostingDates.has(dateKey);
            const isGoing = goingDates.has(dateKey);

            // Determine styles based on priority: Today > Hosting > Going
            let bg = 'transparent';
            let color = '#666';
            let border = 'none';

            if (isToday) {
                bg = '#533B7D';
                color = 'white';
            } else if (isHosting) {
                bg = 'rgba(83, 59, 125, 0.1)';
                color = '#533B7D';
                border = '1px solid #533B7D';
            } else if (isGoing) {
                bg = 'rgba(46, 125, 50, 0.1)';
                color = '#2E7D32';
                border = '1px solid #2E7D32';
            }

            days.push(
                <Box
                    key={i}
                    sx={{
                        width: '100%',
                        maxWidth: 36,
                        aspectRatio: '1',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: bg,
                        color: color,
                        border: border,
                        cursor: 'pointer',
                        fontSize: { xs: '12px', sm: '14px' },
                        fontWeight: (isToday || isHosting || isGoing) ? 'bold' : 'normal',
                        '&:hover': {
                            backgroundColor: isToday ? '#533B7D' : '#f5f5f5'
                        }
                    }}
                >
                    {i}
                </Box>
            );
        }

        return days;
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                {/* Left Sidebar - Calendar */}
                <Grid item xs={12} md={4} lg={4} sx={{ minWidth: 0 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, sm: 2.5, md: 3 },
                            borderRadius: 4,
                            backgroundColor: '#F3F0F7', // Light purple background from screenshot
                            mb: { xs: 2, md: 3 },
                            maxWidth: '100%'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                            </Typography>
                            <Box>
                                <IconButton size="small" onClick={prevMonth}>
                                    <ChevronLeft fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={nextMonth}>
                                    <ChevronRight fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1, minWidth: 0 }}>
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                                <Typography
                                    key={index}
                                    variant="caption"
                                    align="center"
                                    sx={{ color: '#666', fontWeight: 'bold' }}
                                >
                                    {day}
                                </Typography>
                            ))}
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, minWidth: 0 }}>
                            {renderCalendar()}
                        </Box>
                    </Paper>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#533B7D' }} />
                            <Typography variant="body2">Today</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'rgba(83, 59, 125, 0.1)', border: '1px solid #533B7D' }} />
                            <Typography variant="body2">My Events</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'rgba(46, 125, 50, 0.1)', border: '1px solid #2E7D32' }} />
                            <Typography variant="body2">Joined Events</Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Main Content */}
                <Grid item xs={12} md={8} lg={8}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      mb: { xs: 2, md: 3 }
                    }}>
                        <Typography variant="h4" fontWeight="bold" sx={{
                          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
                        }}>
                            Upcoming Events
                        </Typography>
                        {/* <Button
                            variant="text"
                            sx={{ color: '#333', textTransform: 'none' }}
                            onClick={() => {
                                // Logic to view past events could go here, or just scroll down
                                // For now, maybe just a placeholder or toggle
                            }}
                        >
                            view past events
                        </Button> */}
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 0,
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: 'transparent',
                            mb: 4
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#533B7D',
                                    height: 3,
                                    borderRadius: '3px 3px 0 0'
                                },
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    color: '#666',
                                    '&.Mui-selected': {
                                        color: '#533B7D'
                                    }
                                }
                            }}
                        >
                            <Tab label="My Events" />
                            <Tab label="Joined Events" />
                        </Tabs>
                    </Paper>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress sx={{ color: '#533B7D' }} />
                        </Box>
                    ) : (
                        <Box>
                            {/* My Events (hosted) */}
                            {activeTab === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {upcomingHostingEvents.length > 0 ? (
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Upcoming</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                {upcomingHostingEvents.map((event) => (
                                                    <EventCard
                                                        key={event.id}
                                                        event={event}
                                                        variant="horizontal"
                                                        isHost={true}
                                                        onClick={() => navigate(`/event/${event.id}`)}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography color="text.secondary">No upcoming events hosted.</Typography>
                                    )}

                                    {pastHostingEvents.length > 0 && (
                                        <Box sx={{ mt: 4 }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#666' }}>Past Events</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                {pastHostingEvents.map((event) => (
                                                    <EventCard
                                                        key={event.id}
                                                        event={event}
                                                        variant="horizontal"
                                                        isHost={true}
                                                        onClick={() => navigate(`/event/${event.id}`)}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Joined Events */}
                            {activeTab === 1 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {upcomingGoingEvents.length > 0 ? (
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Today</Typography>
                                            {/* Grouping by date logic could be added here, for now just list them */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                {upcomingGoingEvents.map(({ event, status }) => (
                                                    <EventCard
                                                        key={event.id}
                                                        event={event}
                                                        variant="horizontal"
                                                        showStatus={true}
                                                        userStatus={status}
                                                        onClick={() => navigate(`/event/${event.id}`)}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography color="text.secondary">No upcoming events joined.</Typography>
                                    )}

                                    {pastGoingEvents.length > 0 && (
                                        <Box sx={{ mt: 4 }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#666' }}>Past Events</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                {pastGoingEvents.map(({ event, status }) => (
                                                    <EventCard
                                                        key={event.id}
                                                        event={event}
                                                        variant="horizontal"
                                                        showStatus={true}
                                                        userStatus={status}
                                                        onClick={() => navigate(`/event/${event.id}`)}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};

export default MyEvents;
